/**
 * TagChip Component for Voice2Doc Tags System
 * 
 * Features:
 * - Multiple sizes: sm, md, lg
 * - Color coding from TAG_COLORS palette
 * - Optional delete functionality
 * - Mobile-first responsive design
 * - Light/dark theme support
 * - Hover effects and transitions
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, MoreHorizontal, Edit2, Trash2, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tag } from '@/integrations/supabase/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from "react-i18next";

const tagChipVariants = cva(
  "inline-flex items-center gap-1 rounded-full border font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 select-none",
  {
    variants: {
      size: {
        sm: "px-2 py-1 text-xs min-h-[24px]",
        md: "px-3 py-1.5 text-sm min-h-[32px]",
        lg: "px-4 py-2 text-base min-h-[40px]",
      },
      color: {
        red: "bg-red-100 text-red-700 border-red-200 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-900/30",
        orange: "bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800 dark:hover:bg-orange-900/30",
        yellow: "bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800 dark:hover:bg-yellow-900/30",
        green: "bg-green-100 text-green-700 border-green-200 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800 dark:hover:bg-green-900/30",
        blue: "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900/30",
        purple: "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800 dark:hover:bg-purple-900/30",
        pink: "bg-pink-100 text-pink-700 border-pink-200 hover:bg-pink-200 dark:bg-pink-900/20 dark:text-pink-300 dark:border-pink-800 dark:hover:bg-pink-900/30",
        default: "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800/70",
      },
      deletable: {
        true: "pr-1",
        false: "",
      },
    },
    defaultVariants: {
      size: "md",
      color: "default",
      deletable: false,
    },
  }
);

const deleteButtonVariants = cva(
  "inline-flex items-center justify-center rounded-full transition-all duration-150 hover:scale-110 active:scale-95 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-current",
  {
    variants: {
      size: {
        sm: "h-4 w-4 ml-1",
        md: "h-5 w-5 ml-1.5",
        lg: "h-6 w-6 ml-2",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

// Map hex colors to variant names
const colorToVariant = {
  '#EF4444': 'red',
  '#F97316': 'orange',
  '#EAB308': 'yellow',
  '#22C55E': 'green',
  '#3B82F6': 'blue',
  '#8B5CF6': 'purple',
  '#EC4899': 'pink',
} as const;

export interface TagChipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
  VariantProps<typeof tagChipVariants> {
  tag: Tag;
  onDelete?: (tagId: string) => void;
  onEdit?: (tag: Tag) => void;
  onRemove?: (tag: Tag) => void;
  onNavigateToRecords?: (tag: Tag) => void;
  maxWidth?: string;
  truncate?: boolean;
  showContextMenu?: boolean;
  showContextMenuButton?: boolean; // Показывать кнопку с троеточием для контекстного меню
}

const TagChip = React.forwardRef<HTMLDivElement, TagChipProps>(
  ({ tag, onDelete, onEdit, onRemove, onNavigateToRecords, size = "md", className, maxWidth, truncate = true, showContextMenu = false, showContextMenuButton = false, ...props }, ref) => {
    const { t } = useTranslation();

    // Проверяем, является ли цвет предустановленным или произвольным
    const colorVariant = colorToVariant[tag.color as keyof typeof colorToVariant];
    const isCustomColor = !colorVariant;
    const deletable = Boolean(onDelete);
    const hasContextMenu = showContextMenu && (onEdit || onRemove || onNavigateToRecords);
    const hasContextMenuButton = showContextMenuButton && (onEdit || onRemove || onNavigateToRecords);

    // Функция для генерации стилей произвольного цвета
    const getCustomColorStyles = (hexColor: string) => {
      // Конвертируем hex в RGB для расчета яркости
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);

      // Вычисляем яркость цвета
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      // Определяем, темный ли цвет
      const isDark = brightness < 128;

      return {
        backgroundColor: `${hexColor}20`, // 20 = ~12% opacity
        color: hexColor,
        borderColor: `${hexColor}40`, // 40 = ~25% opacity
        '--hover-bg': `${hexColor}30`, // 30 = ~18% opacity для hover
      } as React.CSSProperties;
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onDelete) {
        onDelete(tag.id);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && onDelete) {
        e.preventDefault();
        e.stopPropagation();
        onDelete(tag.id);
      }
    };

    // Если нужна кнопка с троеточием для контекстного меню
    if (hasContextMenuButton && !deletable) {
      return (
        <div
          ref={ref}
          className={cn(
            tagChipVariants({
              size,
              color: isCustomColor ? 'default' : colorVariant,
              deletable: false
            }),
            truncate && "max-w-full",
            // Кастомные стили для произвольных цветов
            isCustomColor && "border-2 hover:opacity-90 transition-opacity",
            className
          )}
          style={{
            ...(maxWidth ? { maxWidth } : {}),
            ...(isCustomColor ? getCustomColorStyles(tag.color) : {})
          }}
          onKeyDown={handleKeyDown}
          aria-label={`Tag: ${tag.name}`}
          {...props}
        >
          <span
            className={cn(
              "flex-1 min-w-0",
              truncate && "truncate"
            )}
            title={tag.name}
          >
            {tag.name}
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "inline-flex items-center justify-center rounded-full transition-all duration-150 hover:bg-black/10 dark:hover:bg-white/10 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-current ml-1",
                  size === "sm" && "h-4 w-4",
                  size === "md" && "h-5 w-5",
                  size === "lg" && "h-6 w-6"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                aria-label={t('records.tag_options_label', { name: tag.name })}
                type="button"
              >
                <MoreHorizontal className={cn(
                  "transition-all duration-150",
                  size === "sm" && "h-3 w-3",
                  size === "md" && "h-3.5 w-3.5",
                  size === "lg" && "h-4 w-4"
                )} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="bottom">
              {onNavigateToRecords && (
                <DropdownMenuItem
                  onClick={() => {
                    onNavigateToRecords(tag);
                  }}
                  className="cursor-pointer"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t('records.filter_by_tag')}
                </DropdownMenuItem>
              )}
              {onEdit && (
                <DropdownMenuItem
                  onClick={() => {
                    onEdit(tag);
                  }}
                  className="cursor-pointer"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  {t('records.edit_tag')}
                </DropdownMenuItem>
              )}
              {onRemove && (
                <DropdownMenuItem
                  onClick={() => {
                    onRemove(tag);
                  }}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('records.remove_tag')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }

    // Если есть контекстное меню и нет функции удаления, делаем весь тег кликабельным
    if (hasContextMenu && !deletable) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              ref={ref}
              className={cn(
                tagChipVariants({
                  size,
                  color: isCustomColor ? 'default' : colorVariant,
                  deletable: false
                }),
                truncate && "max-w-full",
                // Кастомные стили для произвольных цветов
                isCustomColor && "border-2 hover:opacity-90 transition-opacity",
                "cursor-pointer hover:opacity-80",
                className
              )}
              style={{
                ...(maxWidth ? { maxWidth } : {}),
                ...(isCustomColor ? getCustomColorStyles(tag.color) : {})
              }}
              onKeyDown={handleKeyDown}
              role="button"
              aria-label={`Tag: ${tag.name}. Click to see options`}
              {...props}
            >
              <span
                className={cn(
                  "flex-1 min-w-0",
                  truncate && "truncate"
                )}
                title={tag.name}
              >
                {tag.name}
              </span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom">
            {onNavigateToRecords && (
              <DropdownMenuItem
                onClick={() => {
                  onNavigateToRecords(tag);
                }}
                className="cursor-pointer"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('records.filter_by_tag')}
              </DropdownMenuItem>
            )}
            {onEdit && (
              <DropdownMenuItem
                onClick={() => {
                  onEdit(tag);
                }}
                className="cursor-pointer"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                {t('records.edit_tag')}
              </DropdownMenuItem>
            )}
            {onRemove && (
              <DropdownMenuItem
                onClick={() => {
                  onRemove(tag);
                }}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('records.remove_tag')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // Обычный тег без контекстного меню или с функцией удаления
    return (
      <div
        ref={ref}
        className={cn(
          tagChipVariants({
            size,
            color: isCustomColor ? 'default' : colorVariant,
            deletable
          }),
          truncate && "max-w-full",
          // Кастомные стили для произвольных цветов
          isCustomColor && "border-2 hover:opacity-90 transition-opacity",
          className
        )}
        style={{
          ...(maxWidth ? { maxWidth } : {}),
          ...(isCustomColor ? getCustomColorStyles(tag.color) : {})
        }}
        onKeyDown={handleKeyDown}
        tabIndex={deletable ? 0 : undefined}
        role={deletable ? "button" : undefined}
        aria-label={deletable ? `Tag: ${tag.name}. Press Delete or Backspace to remove` : `Tag: ${tag.name}`}
        {...props}
      >
        <span
          className={cn(
            "flex-1 min-w-0",
            truncate && "truncate"
          )}
          title={tag.name}
        >
          {tag.name}
        </span>

        {deletable && (
          <button
            onClick={handleDeleteClick}
            className={cn(deleteButtonVariants({ size }))}
            aria-label={t('records.remove_tag_aria', { name: tag.name })}
            type="button"
          >
            <X className={cn(
              "transition-all duration-150",
              size === "sm" && "h-3 w-3",
              size === "md" && "h-3.5 w-3.5",
              size === "lg" && "h-4 w-4"
            )} />
          </button>
        )}
      </div>
    );
  }
);
TagChip.displayName = "TagChip";

// eslint-disable-next-line react-refresh/only-export-components
export { TagChip, tagChipVariants };